import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import '../../styles/Modal.css';

/**
 * Modal para cambio de contraseña
 * Puede ser obligatorio (cuando debeCambiarPassword=true) o voluntario
 */
function CambiarPasswordModal({
  isOpen,
  onClose,
  onSuccess,
  obligatorio = false
}) {
  const [formData, setFormData] = useState({
    contrasenaActual: '',
    contrasenaNueva: '',
    confirmarContrasena: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    actual: false,
    nueva: false,
    confirmar: false
  });

  const inputRef = useRef(null);

  // Focus en el primer input al abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Limpiar formulario al cerrar
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        contrasenaActual: '',
        contrasenaNueva: '',
        confirmarContrasena: ''
      });
      setError('');
      setShowPasswords({ actual: false, nueva: false, confirmar: false });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones del frontend
    if (!formData.contrasenaActual || !formData.contrasenaNueva || !formData.confirmarContrasena) {
      setError('Todos los campos son obligatorios');
      return;
    }

    if (formData.contrasenaNueva.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.contrasenaNueva !== formData.confirmarContrasena) {
      setError('La nueva contraseña y su confirmación no coinciden');
      return;
    }

    if (formData.contrasenaActual === formData.contrasenaNueva) {
      setError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/cambiar-password', formData);

      if (response.success) {
        // Actualizar el usuario en storage para reflejar que ya no debe cambiar password
        const sessionUser = sessionStorage.getItem('user');
        const localUser = localStorage.getItem('user');

        if (sessionUser) {
          const user = JSON.parse(sessionUser);
          user.debeCambiarPassword = false;
          sessionStorage.setItem('user', JSON.stringify(user));
        }
        if (localUser) {
          const user = JSON.parse(localUser);
          user.debeCambiarPassword = false;
          localStorage.setItem('user', JSON.stringify(user));
        }

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setError(err.message || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={obligatorio ? undefined : onClose}>
      <div
        className="modal-container modal-form"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-icon warning">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <h3 className="modal-title">
          {obligatorio ? 'Cambio de Contraseña Obligatorio' : 'Cambiar Contraseña'}
        </h3>

        {obligatorio && (
          <p className="modal-message">
            Por seguridad, debes cambiar tu contraseña antes de continuar.
          </p>
        )}

        <form onSubmit={handleSubmit} className="modal-form-content">
          <div className="modal-form-group">
            <label htmlFor="contrasenaActual">Contraseña Actual</label>
            <div className="password-input-wrapper">
              <input
                ref={inputRef}
                type={showPasswords.actual ? 'text' : 'password'}
                id="contrasenaActual"
                name="contrasenaActual"
                value={formData.contrasenaActual}
                onChange={handleChange}
                placeholder="Ingresa tu contraseña actual"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => toggleShowPassword('actual')}
                tabIndex={-1}
              >
                {showPasswords.actual ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="modal-form-group">
            <label htmlFor="contrasenaNueva">Nueva Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.nueva ? 'text' : 'password'}
                id="contrasenaNueva"
                name="contrasenaNueva"
                value={formData.contrasenaNueva}
                onChange={handleChange}
                placeholder="Ingresa tu nueva contraseña"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => toggleShowPassword('nueva')}
                tabIndex={-1}
              >
                {showPasswords.nueva ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="modal-form-group">
            <label htmlFor="confirmarContrasena">Confirmar Nueva Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirmar ? 'text' : 'password'}
                id="confirmarContrasena"
                name="confirmarContrasena"
                value={formData.confirmarContrasena}
                onChange={handleChange}
                placeholder="Confirma tu nueva contraseña"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => toggleShowPassword('confirmar')}
                tabIndex={-1}
              >
                {showPasswords.confirmar ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="modal-error">{error}</div>}

          <div className="modal-actions">
            {!obligatorio && (
              <button
                type="button"
                className="btn-modal-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="btn-modal-confirm success"
              disabled={loading}
              style={obligatorio ? { width: '100%' } : {}}
            >
              {loading ? 'Guardando...' : 'Cambiar Contraseña'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CambiarPasswordModal;
